import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
    Plus, Trash2, Search, Calendar as CalendarIcon,
    Pencil, Filter, List, Check, ChevronLeft, ChevronRight
} from "lucide-react";
import { handleGet, handleRemove } from "../../../controllers/controller.all";
import Pagination from "../../../components/ui/Pagination";
import SortableHeader from "../../../components/ui/SortableHeader";

// ─── Constants ────────────────────────────────────────────────────────────────
const START_HOUR = 7;
const END_HOUR = 22;          // affiche jusqu'à 22h (cours du soir inclus)
const HOUR_HEIGHT = 72;       // px par heure
const DAYS_FR = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Retourne le lundi de la semaine contenant la date d */
function getMonday(d) {
    const date = new Date(d);
    date.setHours(0, 0, 0, 0);
    const day = date.getDay(); // 0=dim, 1=lun…
    const diff = day === 0 ? -6 : 1 - day;
    date.setDate(date.getDate() + diff);
    return date;
}

/** Formate une Date JS en "YYYY-MM-DD" */
function toISO(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

/**
 * Vérifie si un planning appartient à une date donnée.
 * Supporte :
 *   - format ISO "YYYY-MM-DD"  → comparaison directe
 *   - noms de jours "lundi"…   → récurrent, s'affiche chaque semaine correspondante
 */
function itemMatchesDate(item, isoDate, dayName) {
    const j = (item.jour || "").trim();
    if (!j) return false;
    // Format ISO → spécifique à cette date
    if (/^\d{4}-\d{2}-\d{2}$/.test(j)) return j === isoDate;
    // Nom de jour → récurrent
    if (j.toLowerCase() === dayName.toLowerCase()) {
        if (item.date_fin && isoDate > item.date_fin) {
            return false; // La récurrence est terminée
        }
        return true;
    }
    return false;
}

/** Calcule la position top (px) d'un horaire "HH:MM" dans la grille */
function getTopPosition(timeStr) {
    if (!timeStr) return 0;
    const [h, m] = timeStr.split(":").map(Number);
    const clampedH = Math.max(START_HOUR, Math.min(h, END_HOUR));
    return ((clampedH - START_HOUR) + (m / 60)) * HOUR_HEIGHT;
}

/** Calcule la hauteur (px) entre deux horaires "HH:MM" */
function getHeight(startStr, endStr) {
    if (!startStr || !endStr) return HOUR_HEIGHT;
    const [h1, m1] = startStr.split(":").map(Number);
    const [h2, m2] = endStr.split(":").map(Number);
    const diffMins = Math.max(30, (h2 * 60 + m2) - (h1 * 60 + m1));
    return (diffMins / 60) * HOUR_HEIGHT;
}

/** Couleur déterministe selon l'id de classe */
const CLASS_COLORS = [
    "bg-blue-100 border-blue-300 text-blue-900",
    "bg-green-100 border-green-300 text-green-900",
    "bg-purple-100 border-purple-300 text-purple-900",
    "bg-pink-100 border-pink-300 text-pink-900",
    "bg-yellow-100 border-yellow-300 text-yellow-900",
    "bg-indigo-100 border-indigo-300 text-indigo-900",
    "bg-red-100 border-red-300 text-red-900",
    "bg-teal-100 border-teal-300 text-teal-900",
    "bg-orange-100 border-orange-300 text-orange-900",
];
function getClassColor(classId) {
    if (!classId) return "bg-gray-100 border-gray-200 text-gray-800";
    let hash = 0;
    for (let i = 0; i < classId.length; i++) {
        hash = classId.charCodeAt(i) + ((hash << 5) - hash);
    }
    return CLASS_COLORS[Math.abs(hash) % CLASS_COLORS.length];
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function PlanningList() {
    const user      = useSelector((s) => s.userReducer?.user);
    const token     = user?.access_token || "";
    const ecoleId   = user?.ecole?._id || user?.ecole;

    // ── Data ──────────────────────────────────────────────────────────────────
    const [items,    setItems]    = useState([]);
    const [classes,  setClasses]  = useState([]);
    const [matieres, setMatieres] = useState([]);
    const [loading,  setLoading]  = useState(true);

    // ── UI state ──────────────────────────────────────────────────────────────
    const [viewMode,          setViewMode]          = useState("calendar");
    const [filterClasse,      setFilterClasse]      = useState("");   // ID unique
    const [showClassDropdown, setShowClassDropdown] = useState(false);
    const [filterMatiere,     setFilterMatiere]     = useState("");
    const [searchTerm,        setSearchTerm]        = useState("");

    // ── Calendar navigation ───────────────────────────────────────────────────
    const [currentWeekStart, setCurrentWeekStart] = useState(() => getMonday(new Date()));

    // ── List pagination & sort ────────────────────────────────────────────────
    const [currentPage, setCurrentPage] = useState(1);
    const [sortKey,     setSortKey]     = useState("jour");
    const [sortOrder,   setSortOrder]   = useState("asc");
    const itemsPerPage = 20;

    // ── Fetching ──────────────────────────────────────────────────────────────
    const fetchRefs = useCallback(async () => {
        await Promise.all([
            handleGet(`/classe/ecole/${ecoleId}`,  (r) => { if (r?.classes)  setClasses(r.classes); },  token),
            handleGet(`/matiere/ecole/${ecoleId}`, (r) => { if (r?.matieres) setMatieres(r.matieres); }, token)
        ]);
    }, [ecoleId, token]);

    const fetchItems = useCallback(async () => {
        setLoading(true);
        await handleGet(
            `/planning/ecole/${ecoleId}?skip=0&limit=100000`,
            (r) => {
                if (r?.plannings) {
                    setItems(r.plannings.filter(
                        (i) => i.ecole === ecoleId || i.ecole?._id === ecoleId
                    ));
                }
            },
            token
        );
        setLoading(false);
    }, [ecoleId, token]);

    useEffect(() => {
        if (token) { fetchRefs(); fetchItems(); }
    }, [fetchRefs, fetchItems, token]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleDelete = async (id) => handleRemove(`/planning/${id}`, fetchItems, token);

    const handlePrevWeek = () => {
        setCurrentWeekStart((prev) => {
            const d = new Date(prev);
            d.setDate(d.getDate() - 7);
            return d;
        });
    };
    const handleNextWeek = () => {
        setCurrentWeekStart((prev) => {
            const d = new Date(prev);
            d.setDate(d.getDate() + 7);
            return d;
        });
    };
    const handleToday = () => setCurrentWeekStart(getMonday(new Date()));

    // ── Computed dates for the current week ───────────────────────────────────
    const weekDates = useMemo(() => {
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(currentWeekStart);
            d.setDate(d.getDate() + i);
            return d;
        });
    }, [currentWeekStart]);

    // ── Filtered items (shared for list + calendar) ───────────────────────────
    const filteredItems = useMemo(() => {
        return items.filter((item) => {
            const classId   = item.classe?._id || item.classe;
            const matiereId = item.matiere?._id || item.matiere;

            const matchClasse  = !filterClasse  || classId   === filterClasse;
            const matchMatiere = !filterMatiere || matiereId === filterMatiere ||
                                 item.matiere   === filterMatiere;
            const q = searchTerm.toLowerCase();
            const matchSearch  = !q ||
                (item.classe?.libelle || "").toLowerCase().includes(q) ||
                (item.matiere?.libelle || "").toLowerCase().includes(q) ||
                (item.jour || "").toLowerCase().includes(q);

            return matchClasse && matchMatiere && matchSearch;
        });
    }, [items, filterClasse, filterMatiere, searchTerm]);

    // ── Sort (for list view) ──────────────────────────────────────────────────
    const sortedItems = useMemo(() => {
        return [...filteredItems].sort((a, b) => {
            let vA = a[sortKey] ?? "";
            let vB = b[sortKey] ?? "";
            if (typeof vA === "object" && vA) vA = vA.libelle || vA.nom || "";
            if (typeof vB === "object" && vB) vB = vB.libelle || vB.nom || "";
            vA = String(vA).toLowerCase();
            vB = String(vB).toLowerCase();
            if (vA < vB) return sortOrder === "asc" ? -1 : 1;
            if (vA > vB) return sortOrder === "asc" ?  1 : -1;
            return 0;
        });
    }, [filteredItems, sortKey, sortOrder]);

    const paginatedItems = useMemo(
        () => sortedItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
        [sortedItems, currentPage]
    );

    // ── Calendar renderer ─────────────────────────────────────────────────────
    const renderCalendar = () => {
        const totalHeight = (END_HOUR - START_HOUR) * HOUR_HEIGHT;
        const hours       = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => START_HOUR + i);

        return (
            <div className="flex flex-col w-full bg-gray-50 border-t border-gray-200">
                {/* Calendar toolbar */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border-b border-gray-200 bg-white gap-4">
                    <h2 className="text-base font-semibold text-gray-800 capitalize">
                        Semaine du{" "}
                        {weekDates[0].toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                        {" "}&rarr;{" "}
                        {weekDates[6].toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}
                    </h2>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handlePrevWeek}
                            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-600 transition"
                            title="Semaine precedente"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                            onClick={handleToday}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 text-sm font-medium text-gray-700 transition"
                        >
                            Aujourd'hui
                        </button>
                        <button
                            onClick={handleNextWeek}
                            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-600 transition"
                            title="Semaine suivante"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {/* Grid */}
                <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "75vh" }}>
                    <div className="flex" style={{ minWidth: 900 }}>
                        {/* Time column */}
                        <div className="w-14 flex-shrink-0 bg-white border-r border-gray-200 sticky left-0 z-20">
                            {/* Corner */}
                            <div className="h-14 border-b border-gray-200 bg-gray-50" />
                            {/* Hour labels */}
                            <div className="relative" style={{ height: totalHeight }}>
                                {hours.map((h) => (
                                    <div
                                        key={h}
                                        className="absolute right-2 text-[10px] text-gray-400 font-medium -translate-y-1/2 select-none"
                                        style={{ top: (h - START_HOUR) * HOUR_HEIGHT }}
                                    >
                                        {String(h).padStart(2, "0")}:00
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Days */}
                        <div className="flex-1 grid grid-cols-7">
                            {weekDates.map((date, index) => {
                                const dayName = DAYS_FR[index];
                                const isoDate = toISO(date);
                                const isToday = toISO(new Date()) === isoDate;

                                const dayItems = filteredItems.filter(
                                    (item) => itemMatchesDate(item, isoDate, dayName)
                                );

                                return (
                                    <div
                                        key={isoDate}
                                        className={`border-r border-gray-200 relative min-w-0 ${isToday ? "bg-indigo-50/30" : ""}`}
                                    >
                                        {/* Day header */}
                                        <div
                                            className={`h-14 border-b border-gray-200 flex flex-col items-center justify-center sticky top-0 z-10 bg-gray-50 ${isToday ? "text-indigo-600" : "text-gray-500"}`}
                                        >
                                            <div className="text-[10px] uppercase font-semibold tracking-widest">
                                                {dayName.slice(0, 3)}
                                            </div>
                                            <div
                                                className={`text-lg font-bold mt-0.5 w-8 h-8 flex items-center justify-center rounded-full ${isToday ? "bg-indigo-600 text-white" : ""}`}
                                            >
                                                {date.getDate()}
                                            </div>
                                        </div>

                                        {/* Events container */}
                                        <div className="relative" style={{ height: totalHeight }}>
                                            {/* Horizontal hour lines */}
                                            {hours.slice(0, -1).map((h) => (
                                                <div
                                                    key={h}
                                                    className="absolute w-full border-t border-gray-100"
                                                    style={{ top: (h - START_HOUR) * HOUR_HEIGHT }}
                                                />
                                            ))}

                                            {dayItems.map((item) => {
                                                const top    = getTopPosition(item.heure_debut);
                                                const height = getHeight(item.heure_debut, item.heure_fin);
                                                const classId = item.classe?._id || item.classe;
                                                const color  = getClassColor(classId);

                                                return (
                                                    <div
                                                        key={item._id}
                                                        className={`absolute inset-x-0.5 p-1.5 rounded border overflow-hidden flex flex-col group hover:z-30 hover:shadow-lg transition-shadow ${color}`}
                                                        style={{ top, height }}
                                                        title={`${item.matiere?.libelle} - ${item.heure_debut} a ${item.heure_fin}`}
                                                    >
                                                        <div className="text-[9px] font-bold opacity-70 leading-none whitespace-nowrap">
                                                            {item.heure_debut} - {item.heure_fin}
                                                        </div>
                                                        <div className="font-semibold text-[11px] leading-tight truncate mt-0.5">
                                                            {item.matiere?.libelle}
                                                        </div>
                                                        {height >= 55 && (
                                                            <div className="text-[10px] opacity-80 truncate mt-0.5">
                                                                {item.classe?.libelle}
                                                            </div>
                                                        )}
                                                        {height >= 80 && item.salle?.libelle && (
                                                            <div className="text-[9px] opacity-70 truncate">
                                                                {item.salle.libelle}
                                                            </div>
                                                        )}

                                                        {/* Hover actions */}
                                                        <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-white/95 rounded p-0.5 shadow border border-gray-100">
                                                            <Link
                                                                to={`/plannings/update/${item._id}`}
                                                                className="text-indigo-600 hover:bg-indigo-50 p-1 rounded"
                                                                title="Modifier"
                                                            >
                                                                <Pencil className="h-3 w-3" />
                                                            </Link>
                                                            <button
                                                                onClick={() => handleDelete(item._id)}
                                                                className="text-red-500 hover:bg-red-50 p-1 rounded"
                                                                title="Supprimer"
                                                            >
                                                                <Trash2 className="h-3 w-3" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ── Main render ───────────────────────────────────────────────────────────
    return (
        <div className="flex flex-col gap-6">
            {/* Page header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Plannings</h1>
                    <p className="text-sm text-gray-500 mt-1">Gerez les emplois du temps des classes.</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="bg-white border border-gray-200 rounded-lg flex p-1 shadow-sm">
                        <button
                            onClick={() => setViewMode("list")}
                            className={`p-1.5 rounded-md flex items-center justify-center transition-colors ${viewMode === "list" ? "bg-indigo-100 text-indigo-700" : "text-gray-500 hover:bg-gray-50"}`}
                            title="Vue Liste"
                        >
                            <List className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => setViewMode("calendar")}
                            className={`p-1.5 rounded-md flex items-center justify-center transition-colors ${viewMode === "calendar" ? "bg-indigo-100 text-indigo-700" : "text-gray-500 hover:bg-gray-50"}`}
                            title="Vue Calendrier"
                        >
                            <CalendarIcon className="h-4 w-4" />
                        </button>
                    </div>
                    <Link
                        to="/plannings/create"
                        className="inline-flex items-center gap-2 bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 rounded-lg transition"
                    >
                        <Plus className="h-4 w-4" /> Nouveau Planning
                    </Link>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Filters toolbar */}
                <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row gap-3 items-center justify-between bg-gray-50">
                    {/* Search */}
                    <div className="relative max-w-sm w-full text-gray-400">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" />
                        <input
                            type="text"
                            placeholder="Rechercher (jour, classe, matiere)..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>

                    <div className="flex gap-2 w-full sm:w-auto">
                        {/* Class filter - single select */}
                        <div className="relative">
                            <button
                                onClick={() => setShowClassDropdown((v) => !v)}
                                className="flex items-center gap-2 border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition min-w-[160px] justify-between"
                            >
                                <span className="flex items-center gap-1.5 truncate">
                                    <Filter className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                    {filterClasse
                                        ? (classes.find((c) => c._id === filterClasse)?.libelle ?? "Classe")
                                        : "Toutes les classes"}
                                </span>
                                {filterClasse && (
                                    <span
                                        className="ml-1 text-indigo-600 hover:text-indigo-800 font-bold"
                                        onClick={(e) => { e.stopPropagation(); setFilterClasse(""); }}
                                        title="Effacer le filtre"
                                    >x</span>
                                )}
                            </button>

                            {showClassDropdown && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setShowClassDropdown(false)} />
                                    <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                                        <div
                                            className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer text-sm text-gray-600 border-b"
                                            onClick={() => { setFilterClasse(""); setShowClassDropdown(false); }}
                                        >
                                            Toutes les classes
                                        </div>
                                        {classes.map((c) => (
                                            <div
                                                key={c._id}
                                                className={`px-4 py-2.5 cursor-pointer text-sm flex items-center justify-between transition-colors ${filterClasse === c._id ? "bg-indigo-50 text-indigo-700 font-medium" : "text-gray-700 hover:bg-gray-50"}`}
                                                onClick={() => {
                                                    setFilterClasse(filterClasse === c._id ? "" : c._id);
                                                    setShowClassDropdown(false);
                                                }}
                                            >
                                                <span>{c.libelle}</span>
                                                {filterClasse === c._id && <Check className="h-4 w-4 text-indigo-600" />}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Matiere filter */}
                        <div className="flex items-center gap-2 border border-gray-300 bg-white rounded-lg px-3 py-2">
                            <Filter className="h-4 w-4 text-gray-400" />
                            <select
                                value={filterMatiere}
                                onChange={(e) => setFilterMatiere(e.target.value)}
                                className="text-sm border-none focus:ring-0 outline-none bg-transparent cursor-pointer text-gray-700"
                            >
                                <option value="">Toutes les matieres</option>
                                {matieres.map((m) => (
                                    <option key={m._id} value={m._id}>{m.libelle}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* View content */}
                {viewMode === "calendar" ? (
                    renderCalendar()
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <SortableHeader label="Jour et Heure" sortKey="jour"    currentSort={sortKey} currentOrder={sortOrder} onSort={(k,o) => { setSortKey(k); setSortOrder(o); }} />
                                    <SortableHeader label="Matiere"       sortKey="matiere"  currentSort={sortKey} currentOrder={sortOrder} onSort={(k,o) => { setSortKey(k); setSortOrder(o); }} />
                                    <SortableHeader label="Classe et Salle" sortKey="classe" currentSort={sortKey} currentOrder={sortOrder} onSort={(k,o) => { setSortKey(k); setSortOrder(o); }} />
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr><td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">Chargement...</td></tr>
                                ) : paginatedItems.length === 0 ? (
                                    <tr><td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">Aucun planning trouve.</td></tr>
                                ) : (
                                    paginatedItems.map((item) => (
                                        <tr key={item._id} className="hover:bg-gray-50 transition">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-3">
                                                    <div className={`h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-lg ${getClassColor(item.classe?._id || item.classe)}`}>
                                                        <CalendarIcon className="h-5 w-5" />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900 capitalize">{item.jour}</div>
                                                        <div className="text-xs text-gray-500">{item.heure_debut} - {item.heure_fin}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.matiere?.libelle || "N/A"}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">{item.classe?.libelle || "N/A"}</div>
                                                <div className="text-xs text-gray-500">{item.salle?.libelle || ""}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <Link
                                                    to={`/plannings/update/${item._id}`}
                                                    className="text-indigo-600 hover:text-indigo-800 bg-indigo-50 p-2 rounded-lg transition inline-block mr-2"
                                                    title="Modifier"
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Link>
                                                <button
                                                    onClick={() => handleDelete(item._id)}
                                                    className="text-red-500 hover:text-red-700 bg-red-50 p-2 rounded-lg transition"
                                                    title="Supprimer"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>

                        {filteredItems.length > itemsPerPage && (
                            <Pagination
                                currentPage={currentPage}
                                totalItems={filteredItems.length}
                                itemsPerPage={itemsPerPage}
                                onPageChange={setCurrentPage}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
